import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import './features/dashboard/recupererRendezVous/AppointmentsDashboard.css';

// ─── Imports SYNCHRONES ──────────────────────────────────────────────────────
import AdminLayout from '@/app/layout/AdminLayout.jsx';
import Footer from '@/app/layout/Footer/Footer.js';
import MyNavbar from '@/app/layout/Navbar/Navbar.js';
import AdminRoute from '@/app/router/adminRoute.js';
import PromoteurRoute from '@/app/router/PromoteurRoute.js';
import ChatbotWidget from '@/components/chatboot/ChatbotWidget.jsx';
import { useAuth } from '@/context/AuthContext';
import { AuthProvider } from '@/context/AuthContext.js';
import { FormProvider } from '@/hooks/formecontext.js';

// form defaults
import defaultFormDataBank from '@/features/offers/bank/defaultFormDataBank.js';
import defaultFormDataEntreprise from '@/features/offers/entreprise/defaultFormDataEntreprise.js';
import { defaultFormDataMaison } from '@/features/offers/maison/defaultFormDataMaison.jsx';
import { defaultFormDataTerrain } from '@/features/offers/terrain/terrainDefaultData.js';

import AdminVisites from './features/admin/immo/AdminVisites';
import ImmoDashboardClient from './features/admin/immo/ImmoDashboardClient';
import MigratePage from './features/admin/immo/MigratePage';
import { defaultFormDataAppartement } from './features/offers/appartement/appartementDefaultData';
import ChargilyDashboard from './features/promoteur/chargili';
import ChargilyPaymentFailure from './features/promoteur/ChargilyPaymentFailure';
import ChargilyPaymentPage from './features/promoteur/ChargilyPaymentPage';
import ChargilyPaymentSuccess from './features/promoteur/ChargilyPaymentSuccess';
import UsersList from './pages/UsersList';

// ─── Imports LAZY ────────────────────────────────────────────────────────────

// Public
const HomePage = lazy(() => import('@/features/home').then(m => ({ default: m.HomePage })));
const Login = lazy(() => import('@/features/auth/login/Login.jsx'));
const SignUp = lazy(() => import('@/features/auth/sign-up/SignUp.jsx'));
const ForgotPassword = lazy(() => import('@/features/auth/forgotPassword/forgotPaasword.jsx'));
const VerifyEmail = lazy(() => import('@/features/auth/email-verification/EmailVerificationHandler'));
const TermsAndConditions = lazy(() => import('@/features/auth/sign-up/TermsAndConditions.jsx'));
const PromoteurTerms = lazy(() => import('./features/auth/sign-up/terms/PromoteurTerms'));
const ContactUs2 = lazy(() => import('@/features/admin/contactus/ContactUs'));
const RegisterPromoteur = lazy(() => import('@/features/promoteur/registerPromoteur.jsx'));

// Offres
const Offers = lazy(() => import('@/features/offers/Offers.jsx'));
const OffersFinancement = lazy(() => import('@/features/financement/financement.jsx'));
const Appartement = lazy(() => import('@/features/offers/appartement/appartement.jsx'));
const InfoTerrain = lazy(() => import('@/features/offers/terrain/infoTerrain.jsx'));
const Maison = lazy(() => import('@/features/offers/maison/maison.jsx'));
const Entreprise = lazy(() => import('@/features/offers/entreprise/entreprise.jsx'));
const BankObtention = lazy(() => import('@/features/offers/bank/BankObtention.jsx'));
const BankEligibilite = lazy(() => import('@/features/offers/bank/BankEligibilite.jsx'));
const BankCards = lazy(() => import('@/features/bank/bank.jsx'));
const PayConditionBank = lazy(() => import('@/features/bank/payConditionBank.jsx'));
const SuccessBankPage = lazy(() => import('@/features/bank/SuccessBankPage.jsx'));
const SuccessPageAppartement = lazy(() => import('@/features/offers/appartement/SuccessPageApparetement.jsx'));
const SuccessPageTerrain = lazy(() => import('@/features/offers/terrain/SuccessPageTerrain.jsx'));
const SuccessPageMaison = lazy(() => import('@/features/offers/maison/SuccessPageMaison.jsx'));
const SuccessPageBanque = lazy(() => import('@/features/offers/bank/successPageBankObtention.jsx'));
const SuccessPageBanque2 = lazy(() => import('./features/offers/bank/successPageBankEligibite'));
const SuccessPageEntreprise = lazy(() => import('@/features/offers/entreprise/SuccessPageEntreprise.jsx'));
const Echange = lazy(() => import('@/epayment/echange.jsx'));

// Immobilier
const ImmobilierCategories = lazy(() => import('@/features/immo/ImmobilierCategories.jsx'));
const ProductList = lazy(() => import('@/features/immo/ProductList.jsx'));
const PropertyDetails = lazy(() => import('@/features/immo/immoRendezVous/PropertyDetails'));
const CartPage = lazy(() => import('@/features/immo/CartPage'));
const ImmobilierMap = lazy(() => import('./features/immo/ImmobilierMap'));
const RendezVousPageImmo = lazy(() => import('@/features/immo/rendezvousTest/redezVousImmo.js'));
const SuccessPage22 = lazy(() => import('@/features/immo/rendezvousTest/succPage22.jsx'));

// Rendez-vous
const RendezVousLanding = lazy(() => import('@/features/rendezvous/RendezVousLanding'));
const RendezVousPage = lazy(() => import('@/features/rendezvous/redezVous'));
const SuccessPage = lazy(() => import('@/features/rendezvous/succPage'));

// Actualités
const NewsApp = lazy(() => import('@/features/actualité/NewsApp.jsx'));
const NewsDetail = lazy(() => import('@/features/actualité/newDetail/NewsDetail.jsx'));
const NewsTickerDetail = lazy(() => import('@/features/actualité/vertical/newsTickerDetail.jsx'));
const ActualitePro = lazy(() => import('@/features/actualité/actualité-pro/actualitéPro.jsx'));

// Dashboard utilisateur
const Dashboard = lazy(() => import('@/features/dashboard/dash/Dashbord.js'));
const AppointmentsDashboard = lazy(() => import('@/features/dashboard/recupererRendezVous/AppointmentsDashboard.jsx'));
const DashAppartement = lazy(() => import('@/features/dashboard/recupererAppartement/dashAppartement.jsx'));
const DashTerrain = lazy(() => import('@/features/dashboard/recupererTerrain/dashTerrain.jsx'));
const DashMaison = lazy(() => import('@/features/dashboard/recupererMAison/dashMaison.jsx'));
const DashEntreprise = lazy(() => import('@/features/dashboard/recupererEnreprise/dashEntreprise.jsx'));
const DashBanque = lazy(() => import('@/features/dashboard/recupererBank/DashBanque.jsx'));
const DashBanque2 = lazy(() => import('@/features/dashboard/recupererBank/DashBanqueEligibite.jsx'));
const VisitesTable = lazy(() => import('@/features/dashboard/recupereImmo/VisitesTable.jsx'));
const EditProfile = lazy(() => import('@/features/personnel/edit.jsx'));

// Chat & messaging
const Inbox = lazy(() => import('@/features/chat/Inbox'));
const ChatRoom = lazy(() => import('@/features/chat/ChatRoom'));
const PromoteurInbox = lazy(() => import('@/features/chat/PromoteurInbox'));
const Chatbot = lazy(() => import('@/components/chatboot/chatboot'));
const ChatTemoignage = lazy(() => import('@/components/chatboot/ChatTemoignage.jsx'));
const Temoignage = lazy(() => import('@/components/chatboot/Temoignage.jsx'));
const Testimonials = lazy(() => import('@/components/chatboot/Testimonials.jsx'));

// Divers public
const StatsManual = lazy(() => import('@/components/statsManual/StatsManual.jsx'));

// ─── Automobile ──────────────────────────────────────────────────────────────
const AutomobileList = lazy(() => import('./features/automobiles/AutomobileList'));
const AutomobileDetails = lazy(() => import('./features/automobiles/AutomobileDetails'));
const AutomobileMap = lazy(() => import('./features/automobiles/AutomobileMap'));
const AdminAutomobile = lazy(() => import('./features/automobiles/admin/AdminAutomobile'));
const AutomobileAddPage = lazy(() => import('./features/automobiles/admin/AutomobileAddPage'));
const AutomobileEditPage = lazy(() => import('./features/automobiles/admin/AutomobileEditPage'));

// ─── Admin ───────────────────────────────────────────────────────────────────
const AdminPage = lazy(() => import('@/features/admin/AdminPage.jsx'));
const AdminChats = lazy(() => import('@/features/chat/AdminChats.jsx'));
const AdminChatRoom = lazy(() => import('@/features/chat/AdminChatRoom'));
const Admin22 = lazy(() => import('@/features/admin/dash/admin22.jsx'));
const AdminBanque = lazy(() => import('@/features/admin/dash/AdminBanque.jsx'));
const AdminEntreprise = lazy(() => import('@/features/admin/dash/AdminEntreprise.jsx'));
const AdminMaison = lazy(() => import('@/features/admin/dash/AdminMaison.jsx'));
const AdminTerrain = lazy(() => import('@/features/admin/dash/AdminTerrains.jsx'));
const AdminContactsList = lazy(() => import('@/features/admin/contactus/AdminContactList.jsx'));
const AdminContactDetail = lazy(() => import('@/features/admin/contactus/AdminContactDetail.jsx'));
const AdminUsersTable = lazy(() => import('@/features/admin/listUser/Adminuserstable.jsx'));
const AdminAppartementsPage = lazy(() => import('@/features/admin/pages/AdminAppartementsPage'));
const AdminBanqueEligibilitePage = lazy(() => import('./features/admin/pages/AdminBanqueEligibilitePage'));
const AdminBanqueObtentionPage = lazy(() => import('./features/admin/pages/AdminBanqueObtentionPage'));
const ActualiteAdmin = lazy(() => import('@/features/admin/actualite/actualiteAdmine'));
const UpdateNews = lazy(() => import('@/features/admin/updateNews/UpdateNews.jsx'));
const SliderManager = lazy(() => import('@/features/admin/editPage/editSlider.jsx').then(m => ({ default: m.SliderManager })));
const ImmoAddPage = lazy(() => import('@/features/admin/immo/ImmoAddPage.jsx'));
const ImmoDashboardAdmin = lazy(() => import('@/features/admin/immo/immodashboard.jsx'));
const RecupererRendesVousTous = lazy(() => import('@/features/admin/recupererRendezVous/recupererRendesVousTous.jsx'));
const AppointmentByDate = lazy(() => import('@/features/admin/recupererRendezVous/recupererRendezVousByDate.jsx'));
const AppointmentManager = lazy(() => import('@/features/admin/recupererRendezVous/recupererRendezVousByUserId.jsx'));
const AdminDashboardStats = lazy(() => import('@/features/admin/AdminDashboardStats.jsx'));
// ─── Promoteur ───────────────────────────────────────────────────────────────
const PromoteurHome = lazy(() => import('@/features/admin/immo/PromoteurHome.jsx'));
const ImmoDashboardPromoteur = lazy(() => import('@/features/admin/immo/ImmoDashboardPromoteur.jsx'));
const PromoteurOrdersTable = lazy(() => import('@/features/admin/immo/PromoteurOrdersTable.jsx'));
const AdminPromoteurs = lazy(() => import('@/features/promoteur/adminPromoteur.jsx'));
const AdminSubscriptions = lazy(() => import('@/features/admin/AdminSubscriptions.jsx'));

// 🆕 Nouveau — gestion complète promoteurs
const AdminPromoteursComplet = lazy(() => import('@/features/admin/AdminPromoteursComplet.jsx'));

// ─── Maps & TEST ─────────────────────────────────────────────────────────────
const ClientMap = lazy(() => import('./TEST/map/ClientMap'));
const PromoterMap = lazy(() => import('./TEST/map/PromoterMap'));
const FunctionsDemo = lazy(() => import('@/TEST/FunctionsDemo.jsx'));

// ─── Fallback Suspense ───────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', fontSize: '14px', color: '#888' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #e5e7eb', borderTop: '3px solid #4f46e5', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Debug ───────────────────────────────────────────────────────────────────
function DebugAuth() {
  const { user, role, isLoading } = useAuth();
  useEffect(() => {
    console.log("DEBUG 🔥 USER :", user);
    console.log("DEBUG 🔥 UID  :", user?.uid);
    console.log("DEBUG 🔥 ROLE :", role);
    console.log("DEBUG 🔥 LOAD :", isLoading);
  }, [user, role, isLoading]);
  return null;
}

// ─── Config formulaires ───────────────────────────────────────────────────────
const formConfigs = {
  "/offre/appartement": { defaultFormData: defaultFormDataAppartement, collectionName: "appartementCollection" },
  "/offre/terrains": { defaultFormData: defaultFormDataTerrain, collectionName: "terrainCollection" },
  "/offre/maison": { defaultFormData: defaultFormDataMaison, collectionName: "maisonCollection" },
  "/offre/entreprise": { defaultFormData: defaultFormDataEntreprise, collectionName: "entrepriseCollection" },
  "/offre/financement/bankCondition": { defaultFormData: defaultFormDataBank, collectionName: "bankCollection" },
  "/offre/bank": { defaultFormData: defaultFormDataBank, collectionName: "bankCollection" },
  "/offre/bankEligibilite": { defaultFormData: defaultFormDataBank, collectionName: "bankCollection2" },
};

// ─── App ─────────────────────────────────────────────────────────────────────
function App() {
  const location = useLocation();
  const formConfig = formConfigs[location.pathname];
  const defaultFormData = formConfig?.defaultFormData || {};
  const collectionName = formConfig?.collectionName || "defaultCollection";

  return (
    <FormProvider defaultFormData={defaultFormData} collectionName={collectionName}>
      <MyNavbar />
      <div className="content">
        <Suspense fallback={<PageLoader />}>
          <Routes>

            {/* ── Public ── */}
            <Route path="/" element={<HomePage />} />
            <Route path="/maps" element={<PromoterMap />} />
            <Route path="/clientmaps" element={<ClientMap />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/contactus" element={<ContactUs2 />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/terms/promoteur" element={<PromoteurTerms />} />
            <Route path="/register-promoteur" element={<RegisterPromoteur />} />

            {/* ── Offres ── */}
            <Route path="/offre" element={<Offers />} />
            <Route path="/offre/financement" element={<OffersFinancement />} />
            <Route path="/offre/appartement" element={<Appartement />} />
            <Route path="/offre/terrains" element={<InfoTerrain />} />
            <Route path="/offre/maison" element={<Maison />} />
            <Route path="/offre/entreprise" element={<Entreprise />} />
            <Route path="/offre/bank" element={<BankObtention />} />
            <Route path="/offre/bankEligibilite" element={<BankEligibilite />} />
            <Route path="/offre/financement/bankCondition" element={<BankCards />} />
            <Route path="/offre/financement/succes" element={<SuccessBankPage />} />
            <Route path="/offre/PayConditionBank" element={<PayConditionBank />} />
            <Route path="/SuccessPageAppartement" element={<SuccessPageAppartement />} />
            <Route path="/SuccessPageTerrain" element={<SuccessPageTerrain />} />
            <Route path="/SuccessPageMaison" element={<SuccessPageMaison />} />
            <Route path="/SuccessPageBank" element={<SuccessPageBanque />} />
            <Route path="/SuccessPageBank2" element={<SuccessPageBanque2 />} />
            <Route path="/SuccessPageEntreprise" element={<SuccessPageEntreprise />} />
            <Route path="/echange" element={<Echange />} />
            <Route path="/migrate" element={<MigratePage />} />

            {/* ── Immobilier ── */}
            <Route path="/immobilier" element={<ImmobilierCategories />} />
            <Route path="/immobilier/:category" element={<ProductList />} />
            <Route path="/immo/property/:id" element={<PropertyDetails />} />
            <Route path="/cartPage" element={<CartPage />} />
            <Route path="/carte" element={<ImmobilierMap />} />
            <Route path="/RendezVousPageImmo/:id" element={<RendezVousPageImmo />} />
            <Route path="/successRendezVousImmobilier" element={<SuccessPage22 />} />

            {/* ── Automobile ── */}
            <Route path="/automobiles" element={<AutomobileList />} />
            <Route path="/automobiles/:category" element={<AutomobileList />} />
            <Route path="/automobiles/detail/:id" element={<AutomobileDetails />} />
            <Route path="/automobiles/map" element={<AutomobileMap />} />

            {/* ── Rendez-vous ── */}
            <Route path="/rendezvous" element={<RendezVousLanding />} />
            <Route path="/rendezvous/:packId" element={<RendezVousPage />} />
            <Route path="/successRendezVous/:packId" element={<SuccessPage />} />

            {/* ── Actualités ── */}
            <Route path="/news" element={<NewsApp />} />
            <Route path="/news/:id" element={<NewsDetail />} />
            <Route path="/actualite" element={<ActualitePro />} />
            <Route path="/actualite/:id" element={<NewsTickerDetail />} />

            {/* ── Dashboard utilisateur ── */}
            <Route path="/edit" element={<EditProfile />} />
            <Route path="/dash" element={<Dashboard />} />
            <Route path="/dash/tab" element={<AppointmentsDashboard />} />
            <Route path="/dash/Appartement" element={<DashAppartement />} />
            <Route path="/dash/terrain" element={<DashTerrain />} />
            <Route path="/dash/maison" element={<DashMaison />} />
            <Route path="/dash/entreprise" element={<DashEntreprise />} />
            <Route path="/dash/banque" element={<DashBanque />} />
            <Route path="/dash/banque2" element={<DashBanque2 />} />
            <Route path="/dash/visites" element={<VisitesTable />} />

            {/* ── Chat & social ── */}
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/chat/:chatId" element={<ChatRoom />} />
            <Route path="/chatbot" element={<Chatbot />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/temoinage" element={<Temoignage />} />
            <Route path="/ChatTemoignage" element={<ChatTemoignage />} />
            <Route path="/statsManual" element={<StatsManual />} />
            <Route path="/testFUNCTION" element={<FunctionsDemo />} />

            {/* ── Client dashboard ── */}
            <Route path="/client/dashboard" element={<ImmoDashboardClient />} />

            {/* ── Admin ── */}
            <Route element={<AdminRoute />}>
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/admin/news" element={<UpdateNews />} />
                <Route path="/admin/appartement" element={<AdminAppartementsPage />} />
                <Route path="/admin/terrain" element={<AdminTerrain />} />
                <Route path="/admin/maison" element={<AdminMaison />} />
                <Route path="/admin/entreprise" element={<AdminEntreprise />} />
                <Route path="/admin/banque" element={<AdminBanque />} />
                <Route path="/admin/banque2" element={<AdminBanque />} />
                <Route path="/admin/banque/obtention" element={<AdminBanqueObtentionPage />} />
                <Route path="/admin/banque/eligibilite" element={<AdminBanqueEligibilitePage />} />
                <Route path="/admin/bb2" element={<Admin22 />} />
                <Route path="/admin/rendezvous" element={<RecupererRendesVousTous />} />
                <Route path="/admin/rendezvous/bydate" element={<AppointmentByDate />} />
                <Route path="/admin/rendezvous/byuser" element={<AppointmentManager />} />
                <Route path="/admin/sliderManager" element={<SliderManager />} />
                <Route path="/admin/immo" element={<ImmoAddPage />} />
                <Route path="/admin/gestion-imm" element={<ImmoDashboardAdmin />} />
                <Route path="/admin/contacts" element={<AdminContactsList />} />
                <Route path="/admin/contacts/:id" element={<AdminContactDetail />} />
                <Route path="/admin/list-user" element={<AdminUsersTable />} />
                <Route path="/admin/list-auth" element={<UsersList />} />
                <Route path="/admin/gestion-promoteurs" element={<AdminPromoteurs />} />
                <Route path="/admin/abonnements" element={<AdminSubscriptions />} />
                {/* 🆕 Nouvelle page gestion complète promoteurs */}
                <Route path="/admin/promoteurs-complet" element={<AdminPromoteursComplet />} />
                <Route path="/admin/chats" element={<AdminChats />} />
                <Route path="/admin/chats/:chatId" element={<AdminChatRoom />} />
                <Route path="/admin/automobiles" element={<AdminAutomobile />} />
                <Route path="/admin/automobiles/add" element={<AutomobileAddPage />} />
                <Route path="/admin/automobiles/edit/:id" element={<AutomobileEditPage />} />
                <Route path="/actualiteAdmin" element={<ActualiteAdmin />} />
                <Route path="/admin/visites" element={<AdminVisites />} />
                <Route path="/admin/stats" element={<AdminDashboardStats />} />
              </Route>
            </Route>

            {/* ── Promoteur (protégé) ── */}
            <Route element={<PromoteurRoute />}>
              <Route path="/promoteur" element={<PromoteurHome />} />
              <Route path="/promoteur/dashboard" element={<ImmoDashboardPromoteur />} />
              <Route path="/promoteur/commandes" element={<PromoteurOrdersTable />} />
              <Route path="/promoteur/inbox" element={<PromoteurInbox />} />
              <Route path="/promoteur/epay" element={<ChargilyDashboard />} />
              <Route path="/promoteur/abonnement" element={<ChargilyPaymentPage />} />
            </Route>

            <Route path="/payment-success" element={<ChargilyPaymentSuccess />} />
            <Route path="/payment-failure" element={<ChargilyPaymentFailure />} />
            <Route path="/chargily-payment" element={<ChargilyPaymentPage />} />

          </Routes>
        </Suspense>
        <DebugAuth />
      </div>
      <Footer />
    </FormProvider>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function Root() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ToastContainer />
        <App />
        <ChatbotWidget />
      </BrowserRouter>
    </AuthProvider>
  );
}